import {MailSentStatus, TemplateType} from "../../../generated/graphql";

export enum InvoiceSentStatus {
    NotSent = "NOT_SENT",
    OutdatedVersionSent = "OUTDATED_VERSION_SENT",
    CurrentVersionSent = "CURRENT_VERSION_SENT",
    CurrentVersionOpened = "CURRENT_VERSION_OPENED",
}

export interface InvoiceSentStatusResult {
    status: InvoiceSentStatus;
    lastSendDate: Date | null;
    openedDate: Date | null;
    outdatedSinceDate: Date | null;
}

export interface InvoiceForStatus {
    createDate?: string | null;
    correctionDate?: string | null;
}

export interface NotificationForStatus {
    // GraphQL-Codegen typisiert DateTime als `any` — wir akzeptieren string|Date|null
    // und gehen davon aus, dass die Backend-Repräsentation ein ISO-String ist.
    createDate?: string | null;
    // EmailNotification.templateType + status sind im Codegen als `Maybe<string>`
    // typisiert; wir vergleichen über die Enum-String-Werte, daher reicht `string`.
    templateType?: string | null;
    status?: string | null;
    messageStatus?: {
        Status?: string | null;
        ArrivedAt?: string | null;
    } | null;
}

export interface GetInvoiceSentStatusOptions {
    isReversal?: boolean;
}

const EMPTY_RESULT: InvoiceSentStatusResult = {
    status: InvoiceSentStatus.NotSent,
    lastSendDate: null,
    openedDate: null,
    outdatedSinceDate: null,
};

const OPENED_MAILJET_STATES = ["opened", "clicked"];

// Liefert für den Rechnungs-Block auf der Settlement-Details-Seite einen
// 4-stufigen Status, der vergleicht, ob die zuletzt versendete Rechnungs-Mail
// die aktuell sichtbare Rechnungs-Version (Original oder Korrektur) abdeckt.
//
// Datenquellen sind bereits auf der Seite geladen — kein Schema-Touch, keine
// neue Query. Die Funktion ist pure und idempotent: gleiche Inputs → gleicher
// Output, unabhängig von der Aufrufzeit.
export function getInvoiceSentStatus(
    invoice: InvoiceForStatus | null | undefined,
    notifications: ReadonlyArray<NotificationForStatus> | null | undefined,
    options: GetInvoiceSentStatusOptions = {},
): InvoiceSentStatusResult {
    if (!invoice || !invoice.createDate) {
        return EMPTY_RESULT;
    }

    const expectedTemplate = options.isReversal
        ? TemplateType.SettledBookingCancellation
        : TemplateType.SettlementConfirmation;

    const relevantMails = (notifications ?? []).filter((n) => {
        if (!n || !n.createDate) return false;
        if (n.templateType !== expectedTemplate) return false;
        // Pending/Error zählen nicht als "verschickt" — nur erfolgreiche Versendungen.
        if (n.status !== MailSentStatus.Success) return false;
        return true;
    });

    if (relevantMails.length === 0) {
        return EMPTY_RESULT;
    }

    // Jüngste Mail anhand `createDate` ermitteln (DESC).
    const sorted = [...relevantMails].sort(
        (a, b) => new Date(b.createDate as string).getTime() - new Date(a.createDate as string).getTime(),
    );
    const latestMail = sorted[0];
    const lastSendDate = new Date(latestMail.createDate as string);

    const relevantInvoiceDateStr = invoice.correctionDate ?? invoice.createDate;
    const relevantInvoiceDate = new Date(relevantInvoiceDateStr);

    if (lastSendDate.getTime() < relevantInvoiceDate.getTime()) {
        return {
            status: InvoiceSentStatus.OutdatedVersionSent,
            lastSendDate,
            openedDate: null,
            outdatedSinceDate: invoice.correctionDate ? new Date(invoice.correctionDate) : null,
        };
    }

    const mailjetStatus = latestMail.messageStatus?.Status?.toLowerCase();
    const isOpened = mailjetStatus !== undefined && OPENED_MAILJET_STATES.indexOf(mailjetStatus) !== -1;

    if (isOpened) {
        const arrivedAt = latestMail.messageStatus?.ArrivedAt;
        return {
            status: InvoiceSentStatus.CurrentVersionOpened,
            lastSendDate,
            openedDate: arrivedAt ? new Date(arrivedAt) : lastSendDate,
            outdatedSinceDate: null,
        };
    }

    return {
        status: InvoiceSentStatus.CurrentVersionSent,
        lastSendDate,
        openedDate: null,
        outdatedSinceDate: null,
    };
}
